import { ReviewSortEnum } from '@app/enums';
import { environment } from '@app/environments/environment';
import { EsServiceBase } from '@app/services/abstracts/es-service.base';

import { EsResponseManyModel } from '../models/elastic-search';
import { RatingModel } from '../models/elastic-search/rating.model';

export class EsAccommodationRatingService extends EsServiceBase {
    readonly #accommodationRatingEsIndexName = 'noc-accommodation-rating';
    readonly #accommodationRatingEsIndexVersion = environment.OTA_RATING_ES_VERSION;

    private static _instance: EsAccommodationRatingService;

    protected get indexName(): string {
        return `${this.#accommodationRatingEsIndexName}-${this.#accommodationRatingEsIndexVersion}`;
    }

    public static get instance(): EsAccommodationRatingService {
        if (!this._instance) {
            this._instance = new EsAccommodationRatingService();
        }

        return this._instance;
    }

    public async loadAccommodationRatings(
        id: string | number,
        from = 0,
        size = 5,
        sort: ReviewSortEnum = ReviewSortEnum.MOST_HELPFUL,
        query = '',
    ): Promise<EsResponseManyModel<RatingModel>> {
        const conditions = [
            {
                term: {
                    accommodationId: id,
                },
            },
        ] as Record<string, unknown>[];

        if (query.length) {
            conditions.push({
                bool: {
                    must: [
                        {
                            multi_match: {
                                query: query,
                                fields: [
                                    'content.stemfold',
                                    'content.autocomplete',
                                    'translatedContent.stemfold',
                                    'translatedContent.autocomplete',
                                ],
                                operator: 'and',
                            },
                        },
                    ],
                },
            });
        }

        const highlight = query.length
            ? {
                  require_field_match: false,
                  fields: {
                      content: {
                          number_of_fragments: 0,
                      },
                      'content.stemfold': {
                          number_of_fragments: 0,
                      },
                      'content.autocomplete': {
                          number_of_fragments: 0,
                      },
                      translatedContent: {
                          number_of_fragments: 0,
                      },
                      'translatedContent.stemfold': {
                          number_of_fragments: 0,
                      },
                      'translatedContent.autocomplete': {
                          number_of_fragments: 0,
                      },
                  },
              }
            : undefined;

        return await this.getMany(this.indexName, {
            query: {
                bool: {
                    must: conditions,
                },
            },
            highlight,
            sort: this.generateSortConfig(sort),
            from,
            size,
        });
    }

    private generateSortConfig(sort: ReviewSortEnum) {
        const commonLanguageSort = {
            _script: {
                type: 'number',
                script: {
                    lang: 'painless',
                    source: 'int sortOrder = 0; if (doc["language.code"].value == "pl") {sortOrder = 1} sortOrder;',
                },
                order: 'desc',
            },
        };

        const guestNameSort = {
            _script: {
                type: 'number',
                script: {
                    lang: 'painless',
                    source: 'doc["guest.name.keyword"].size() > 0 ? 0 : 1',
                },
                order: 'asc',
            },
        };

        const textLengthSort = {
            _script: {
                type: 'number',
                script: {
                    lang: 'painless',
                    source: `
                        int textLength = 0;
                        if (doc['text.keyword'].size() > 0) {
                            textLength += doc['text.keyword'].value.length();
                        }
                        if (doc['positive.keyword'].size() > 0) {
                            textLength += doc['positive.keyword'].value.length();
                        }
                        if (doc['negative.keyword'].size() > 0) {
                            textLength += doc['negative.keyword'].value.length();
                        }
                        if (textLength >= 200) {
                            return 3;
                        } else if (textLength >= 100) {
                            return 2;
                        } else if (textLength > 0) {
                            return 1;
                        } else {
                            return 0;
                        }
                    `,
                },
                order: 'desc',
            },
        };

        const dateSort = { date: { order: 'desc' } };

        const ratingAscSort = {
            _script: {
                type: 'number',
                script: {
                    lang: 'painless',
                    source: 'doc["average"].value > 0 ? doc["average"].value : 0',
                },
                order: 'asc',
            },
        };

        const ratingDescSort = {
            _script: {
                type: 'number',
                script: {
                    lang: 'painless',
                    source: 'doc["average"].value > 0 ? doc["average"].value : 0',
                },
                order: 'desc',
            },
        };

        const sortConfigurations = {
            [ReviewSortEnum.MOST_HELPFUL]: [
                commonLanguageSort,
                textLengthSort,
                dateSort,
                guestNameSort,
            ],
            [ReviewSortEnum.NEWEST]: [commonLanguageSort, dateSort, textLengthSort, guestNameSort],
            [ReviewSortEnum.HIGHEST_RATING]: [
                commonLanguageSort,
                ratingDescSort,
                textLengthSort,
                dateSort,
                guestNameSort,
            ],
            [ReviewSortEnum.LOWEST_RATING]: [
                commonLanguageSort,
                ratingAscSort,
                textLengthSort,
                dateSort,
                guestNameSort,
            ],
        };

        return sortConfigurations[sort] ?? sortConfigurations[ReviewSortEnum.MOST_HELPFUL];
    }
}
